import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Phone, Send, ArrowRight, CheckCircle } from "lucide-react";

const CONTACT_PHONE = process.env.REACT_APP_CONTACT_PHONE;
const API_URL = window.location.href.includes("localhost")
  ? process.env.REACT_APP_API_URL_LOCAL
  : process.env.REACT_APP_API_URL_PROD;

function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const ContactusComponent = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    propertyType: "",
    budget: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const WHATSAPP_LINK = "https://chat.whatsapp.com/HjoR601OWZdJ2PFeUrwWdj?mode=ems_share_t";

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const validatePhone = (phone) => /^\d{10}$/.test(phone);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return alert("Name is required");
    if (!validatePhone(formData.phone)) return alert("Enter a valid 10-digit phone number");
    if (!validateEmail(formData.email)) return alert("Enter a valid email address");

    try {
      setLoading(true);
      const res = await fetch(`${API_URL}api/global-form/skyline-properties/inquiry-customers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to submit form");
      setSuccessMessage(data.message || "Inquiry submitted successfully!");
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert(err.message || "Error submitting form");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">

      {/* --- Hero Section --- */}
      <section className="relative bg-gradient-to-br from-neutral-50 to-white overflow-hidden py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-20 flex flex-col-reverse lg:flex-row items-center lg:justify-between gap-12">
          <div className="flex-1 space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-primary leading-tight">
              Discover Your Dream Property in Solapur
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl">
              Verified properties updated daily. Join our WhatsApp group to stay instantly informed about the latest listings.
            </p>
            <div className="flex flex-wrap gap-4 mt-6">
              <Button
                size="lg"
                className="bg-gradient-to-r from-green-500 to-teal-500 text-white border-0 shadow-lg hover:from-teal-500 hover:to-green-500 transition-all duration-300 flex items-center gap-2"
                onClick={() => window.open(WHATSAPP_LINK, "_blank")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="h-5 w-5" fill="currentColor">
                  <path d="M380.9 97.1C339-5.5 224-28 149.3 47.7c-75 75-52.6 189 45 233.7l-25 91.7c-5.7 20.9 17.8 37 35.8 26.5l95.4-57.1c43.9 23.9 97.2 14.1 133.2-22.9 45.4-45.4 46.8-118.3 6.2-168.5zm-114.8 248.8l-53.3 31.9-19.9-28.6c-4.8-6.8-11.3-12-18.6-15.8-17.6-8.5-29.6-26.8-31.2-46.7l-2.2-23.6 109.4 65.1zM377.3 134c-32.6-32.6-79-45.1-123.4-34.1-2.1.5-4.2 1-6.3 1.5-15.3 3.7-29.9 11.1-42.6 22.8-20.6 18.7-33.5 45.5-35.3 74.4-.2 3.2-.5 6.5-.5 9.7 0 28.2 10.5 54.6 29.5 74.6 19 20 45.1 30.5 72.9 30.5 3.2 0 6.5-.2 9.7-.5 29-1.8 55.7-14.7 74.4-35.3 11.7-12.7 19-27.3 22.8-42.6.5-2.1 1-4.2 1.5-6.3 10.9-44.3-1.5-90.8-34.1-123.4z"/>
                </svg>
                Join WhatsApp Group
                <ArrowRight className="w-4 h-4" />
              </Button>

              {/* <Button
                size="lg"
                className="bg-white text-primary border border-primary shadow-md hover:bg-primary hover:text-white transition-all duration-300 flex items-center gap-2"
                onClick={() => window.scrollTo({ top: document.getElementById("contact").offsetTop, behavior: "smooth" })}
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </Button> */}
            </div>
          </div>
        </div>
      </section>

      {/* --- Trust & Benefits Section --- */}
      <section className="py-16 bg-gradient-to-r from-gray-50 to-white">
        <div className="container mx-auto px-6 text-center max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Why Investors Trust Us</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Over 15 years of experience in Solapur with 20,000+ happy clients. Legal support, verified listings, and personalized consultation.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: "📈", title: "High ROI Properties", desc: "Invest with confidence in premium locations." },
              { icon: "🛡️", title: "Verified Listings", desc: "Every property checked and verified for legality." },
              { icon: "🤝", title: "Expert Support", desc: "Personalized guidance at every step of your investment." },
            ].map((item, i) => (
              <div key={i} className="p-6 bg-white border rounded-xl shadow-md hover:shadow-xl transition-all duration-300">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="font-semibold text-xl mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Contact Form Section --- */}
      <section id="contact" className="py-20 bg-gradient-to-br from-neutral-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">

            {/* Contact Info */}
            <div className="space-y-8 animate-fade-up">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Phone className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-primary">Call Us Directly</div>
                  <div className="font-medium text-primary"><b>CEO: Shiva Chilveri</b></div>
                  <div className="text-muted-foreground">{CONTACT_PHONE}</div>
                  <div className="text-muted-foreground text-sm mb-2">Available 10 AM - 9 PM</div>

                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-green-400 to-teal-500 text-white hover:from-teal-500 hover:to-green-400 border-0 shadow-md transition-all duration-300 mt-2"
                    onClick={() => window.open(`tel:${CONTACT_PHONE}`)}
                  >
                    Call Now
                  </Button>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white border border-neutral/10 shadow-lg rounded-2xl p-8 animate-fade-up" style={{ minHeight: "500px" }}>
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <CheckCircle className="text-green-500 w-16 h-16 mb-4 animate-bounce" />
                  <p className="text-green-600 font-semibold text-lg text-center">{successMessage}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6 h-full">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-primary">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-2"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-primary">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-2"
                        placeholder="Your contact number"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-primary">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-2"
                      placeholder="Your email address"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="propertyType" className="text-primary">Property Type</Label>
                      <Select value={formData.propertyType} onValueChange={(value) => setFormData({ ...formData, propertyType: value })}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select property type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="apartment">Apartment</SelectItem>
                          <SelectItem value="bungalow">Bungalow</SelectItem>
                          <SelectItem value="plot">Plot/Land</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="budget" className="text-primary">Budget Range</Label>
                      <Select value={formData.budget} onValueChange={(value) => setFormData({ ...formData, budget: value })}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select budget" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="under-50">Under ₹50 Lakhs</SelectItem>
                          <SelectItem value="50-100">₹50 - ₹1 Crore</SelectItem>
                          <SelectItem value="100-200">₹1 - ₹2 Crores</SelectItem>
                          <SelectItem value="above-200">Above ₹2 Crores</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-primary">Additional Requirements</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="mt-2"
                      placeholder="Tell us about your specific requirements, preferred location, timeline, etc."
                      rows={4}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-luxury font-medium py-3 group"
                    disabled={loading}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {loading ? "Submitting..." : "Send Inquiry"}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>

                  <p className="text-center text-muted-foreground text-sm">
                    By submitting this form, you agree to our privacy policy and terms of service.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* --- Footer / Call to Action --- */}
      <section className="py-16 bg-gradient-to-r from-green-50 to-teal-50 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Ready to Find Your Dream Property?</h2>
        <p className="text-muted-foreground mb-6">
          Join our WhatsApp group for instant updates or contact us directly. Your perfect investment awaits!
        </p>
        <div className="flex justify-center gap-4 flex-wrap">
          <Button
            size="lg"
            className="bg-gradient-to-r from-green-500 to-teal-500 text-white border-0 shadow-lg hover:from-teal-500 hover:to-green-500 transition-all duration-300 flex items-center gap-2"
            onClick={() => window.open(WHATSAPP_LINK, "_blank")}
          >
            Join WhatsApp Group
            <ArrowRight className="w-4 h-4" />
          </Button>
          {/* <Button
            size="lg"
            className="bg-white text-primary border border-primary shadow-md hover:bg-primary hover:text-white transition-all duration-300 flex items-center gap-2"
            onClick={() => window.scrollTo({ top: document.getElementById("contact").offsetTop, behavior: "smooth" })}
          >
            Contact Us
            <ArrowRight className="w-4 h-4" />
          </Button> */}
        </div>
      </section>
    </div>
  );
};

export default ContactusComponent;
